/**
 * Created by mongoose on 8/11/17.
 */
import { BASE_URL } from "../../request";
import CONSTANTS from "../constants";
import ModuleTranslations from "./ModuleTranslations";
import { getTranslatedDefinition } from "../utils";
import { DEFAULT_SUBJECTS } from "./defaultSubjects";

const formTypeHelperText = ({ formValues }) => {
  switch (formValues.type) {
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION:
      return "Se envía cuando se confirma la reserva. Solo puede estar activa una plantilla al mismo tiempo.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED:
      return "Se envía cuando el pago de la reserva ha fallado.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY:
      return "Se envía días después de la estancia del huésped.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY:
      return "Se envía días antes de la llegada del huésped.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT:
      return "Se enviará el email si el huésped había iniciado sesión como usuario loyalty y tenía al menos una reserva en el carrito.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PURCHASE_EVENT:
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.GIFT_CARD:
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME:
      return "To activate the “Loyalty Welcome” email, you must have the loyalty feature enabled in the engine.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME_VALIDATED:
      return "To activate the “Agencies” email, you must have the loyalty feature enabled in the engine.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.QUOTATION_PRICE:
      return "To activate the “Quote Tool”, you MUST contact Support.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.WAITING_LIST_AVAILABLE:
      return "The email will be sent when the hotel has availability.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.UPSELL_AVAILABLE:
      return "To activate the “Upgrade” feature, you MUST contact Support.";
    default:
      return "";
  }
};

const isCartAbandonmentType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT;
const isReservationFailedType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED;
const isReservationAbandonedPaymentType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_ORANGE;
const isPostStayType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY;
const isPreStayType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY;
const isReservationConfirmationType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION;
const isUpsellAvailableType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.UPSELL_AVAILABLE;
const isQuotationPriceType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.QUOTATION_PRICE;
const isWaitingListAvailableType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.WAITING_LIST_AVAILABLE;
const isLoyaltyWelcomeType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME;
const isLoyaltyWelcomeValidatedType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME_VALIDATED;

const ModuleDefinition = {
  name: "Email Templates",
  entityName: CONSTANTS.ENTITY.EMAIL_TEMPLATE.name,
  restConfig: {
    baseURL: `${BASE_URL}`,
    // Templates are NOT cached by the engine, so template actions must not trigger a property
    // cache regeneration (per backend). Only the email configuration (senders/recipients/etc.)
    // is cached and regenerated on its own save.
    create: {
      url: () => `/api/emails/custom/templates/`,
      method: "post",
      headers: ({ propertyTicker }) => ({ "x-property": propertyTicker }),
    },
    update: {
      url: () => `/api/emails/custom/templates/`,
      method: "put",
      headers: ({ propertyTicker }) => ({ "x-property": propertyTicker }),
    },
    fetch: {
      url: ({ hotelTicker, id }) => `/api/emails/custom/${hotelTicker}/templates/${id ? id : ""}`,
      method: "get",
      headers: ({ propertyTicker }) => ({ "x-property": propertyTicker }),
    },
    delete: {
      url: ({ hotelTicker, id }) => `/api/emails/custom/${hotelTicker}/templates/${id}`,
      method: "delete",
      headers: ({ propertyTicker }) => ({ "x-property": propertyTicker }),
    },
  },
  entities: [],
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "translations",
    widget: "translations",
    entityProperty: "translations",
  },
  fields: {
    list: [
      //columns to be used in the table/list display, Order IS important
      // Column order: Active, Email type, Recipient, Template type, Preferred, Motors, Usage.
      // Per-mode visibility is applied in EmailTemplateList (computedFields): motors → aggregator
      // only; uso → individual only.
      // `active` is an inline toggle (US-004). EmailTemplateList.handleToggle blocks aggregator-
      // shared rows and sends a clean DTO to the toggle PUT (the list mapper's display fields must
      // not leak into the request).
      {
        fieldName: "active",
        displayName: "is Active",
        widget: "singleSwitch",
        toggleAction: true,
      },
      {
        fieldName: "type",
        displayName: "Email type",
      },
      {
        fieldName: "emailDestinationType",
        displayName: "emailDestinationType",
      },
      {
        fieldName: "templateType",
        displayName: "Template type",
      },
      {
        fieldName: "preferred",
        displayName: "Default",
      },
      {
        fieldName: "motors",
        displayName: "Motors",
      },
      {
        fieldName: "uso",
        displayName: "Usage",
      },
    ],
    form: [
      //fields to be used in the creation/edit form display, Order IS important
      {
        fieldName: "id",
        widget: "hidden",
      },
      {
        fieldName: "hotelTicker",
        displayName: "hotel Ticker",
        widget: "hidden",
      },
      {
        fieldName: "isActive",
        displayName: "isActive",
        widget: "checkbox",
        defaultValue: false,
      },
      {
        fieldName: "type",
        displayName: "Email Type",
        widget: "selector",
        optionDisplay: {
          RESERVATION_CONFIRMATION: {
            label: "Reservation Confirmation",
          },
          GIFT_CARD: {
            label: "Gift Card",
          },
          PURCHASE_EVENT: {
            label: "Purchase Event",
          },
          POST_STAY: {
            label: "Post-stay",
          },
          PRE_STAY: {
            label: "Pre-stay",
          },
          CART_ABANDONMENT: {
            label: "Cart abandonment",
          },
          WAITING_LIST_AVAILABLE: {
            label: "Waiting List",
          },
          UPSELL_AVAILABLE: {
            label: "Upgrade",
          },
          RESERVATION_STATUS_RED: {
            label: "Failed Payment",
          },
        },
        transform: ({ field, userInfo, formValues }) => {
          const isWitAdmin = userInfo?.isWitAdmin;
          const extraOptions = isWitAdmin
            ? {
                RESERVATION_STATUS_ORANGE: {
                  label: "Proceso de pago abandonado",
                },
              }
            : {};
          return {
            ...field,
            optionDisplay: { ...field.optionDisplay, ...extraOptions },
            className: "negativeMargin",
            helpText: formTypeHelperText({ formValues }),
          };
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
        visible: ({ entity }) => !entity.id,
      },
      {
        fieldName: "delay",
        displayName: "Delay",
        widget: "selector",
        optionDisplay: {
          5: {
            label: "5 minutes",
          },
          10: {
            label: "10 minutes",
          },
          15: {
            label: "15 minutes",
          },
          30: {
            label: "30 minutes",
          },
          45: {
            label: "45 minutes",
          },
          60: {
            label: "60 minutes",
          },
        },
        helpText: "Amount of minutes before an email is sent.",
        placeholder: "Enter Delay",
        visible: ({ formValues }) =>
          isReservationFailedType(formValues.type) ||
          isReservationAbandonedPaymentType(formValues.type),
        defaultValue: 5,
      },
      {
        fieldName: "delayCartAbandonment",
        displayName: "Delay",
        widget: "selector",
        optionDisplay: {
          60: {
            label: "1 hour",
          },
          120: {
            label: "2 hours",
          },
          180: {
            label: "3 hours",
          },
          360: {
            label: "6 hours",
          },
          720: {
            label: "12 hours",
          },
          1440: {
            label: "24 hours",
          },
          2880: {
            label: "48 hours",
          },
        },
        helpText: "Amount of hours before an email is sent.",
        placeholder: "Enter Delay",
        visible: ({ formValues }) => isCartAbandonmentType(formValues.type),
        defaultValue: 60,
      },
      {
        fieldName: "emailDestinationType",
        displayName: "Email Destination Type",
        widget: "selector",
        optionDisplay: {
          ALL: {
            label: "All",
          },
          GUEST: {
            label: "Guest",
          },
          PROPERTY: {
            label: "Property",
          },
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
        visible: ({ formValues }) =>
          !isCartAbandonmentType(formValues.type) &&
          !isUpsellAvailableType(formValues.type) &&
          !isQuotationPriceType(formValues.type) &&
          !isWaitingListAvailableType(formValues.type) &&
          !isLoyaltyWelcomeType(formValues.type) &&
          !isLoyaltyWelcomeValidatedType(formValues.type),
      },
      {
        fieldName: "emailGuestType",
        displayName: "Email Guest Type",
        widget: "hidden",
        optionDisplay: {
          ALL: {
            label: "All",
          },
          LOYALTY_GUEST: {
            label: "Loyalty guest",
          },
          NON_LOYALTY_GUEST: {
            label: "Non loyalty guest",
          },
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.ALL,
        visible: ({ formValues }) => isCartAbandonmentType(formValues.type),
      },
      {
        fieldName: "delayCheckout",
        displayName: "Días posteriores al check-out para el envío",
        widget: "input",
        className: "wbk-daysInput",
        visible: ({ formValues }) => isPostStayType(formValues.type),
      },
      {
        fieldName: "delayCheckin",
        displayName: "Días previos al Check-in para el envío",
        widget: "input",
        className: "wbk-daysInput",
        visible: ({ formValues }) => isPreStayType(formValues.type),
      },
      {
        fieldName: "chronoUnit",
        displayName: "Delay units",
        widget: "hidden",
        optionDisplay: {
          MINUTES: {
            label: "Minutes",
          },
          HOURS: {
            label: "Hours",
          },
          DAYS: {
            label: "Days",
          },
        },
        defaultValue: "MINUTES",
        visible: ({ formValues }) => !isReservationConfirmationType(formValues.type),
      },
      {
        fieldName: "templateType",
        displayName: "Template type",
        widget: "selector",
        optionDisplay: {
          DEFAULT: {
            label: "Default template",
          },
          CUSTOM: {
            label: "Custom template",
          },
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT,
        visible: ({ formValues }) =>
          isReservationConfirmationType(formValues.type) ||
          isCartAbandonmentType(formValues.type) ||
          isUpsellAvailableType(formValues.type) ||
          isQuotationPriceType(formValues.type) ||
          isWaitingListAvailableType(formValues.type) ||
          isLoyaltyWelcomeType(formValues.type) ||
          isLoyaltyWelcomeValidatedType(formValues.type) ||
          isReservationFailedType(formValues.type) ||
          isReservationAbandonedPaymentType(formValues.type),
      },
      {
        fieldName: "singleMailOnMultipleRoomStays",
        description:
          "Para reservas múltiples agrupar confirmaciones en un solo email (Solo visible para WIT)",
        widget: "checkbox",
        defaultValue: false,
        visible: ({ formValues, userInfo }) =>
          userInfo.isWitAdmin && isReservationConfirmationType(formValues.type),
      },
      // ── Mark as default (predeterminada) ─────────────────────────────────────────
      // Inline checkbox (same style as singleMailOnMultipleRoomStays). Shown for every email
      // template: aggregator create/edit, aggregator-shared on a child, and a motor's own
      // templates — the precedence chain is own → shared → default, so an own template must be
      // markable too (specs §4.3, WTT-11688). Uses `description` (rendered inline by the checkbox
      // widget); the ES/FR text is provided in ModuleTranslations.
      {
        fieldName: "preferred",
        description: "Mark as default",
        widget: "checkbox",
        defaultValue: false,
        visible: () => true,
      },
      // Hint under the `preferred` checkbox: a template must be active to become the default
      // (backend rule). Only shown while the template is inactive. Copy comes from
      // ModuleTranslations (per-locale `value`).
      {
        fieldName: "preferredInactiveHint",
        widget: "simpleText",
        visible: ({ formValues }) => !formValues.isActive,
      },
      {
        fieldName: "subject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: {
          en:
            "{{#isModified}}Update: {{/isModified}}{{#reservationStatus.isPending}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}The reservation has been cancelled. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Booking (confirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Booking (confirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ca:
            "{{#isModified}}Modificació: {{/isModified}}{{#reservationStatus.isPending}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Reserva cancel·lada correctament. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          fr:
            "{{#isModified}}Modification: {{/isModified}}{{#reservationStatus.isPending}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}La réservation a été annulée. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Réservation (confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Réservation (confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          eu:
            "{{#isModified}}Modificación: {{/isModified}}{{#reservationStatus.isPending}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Errerserba zuzenki ezeztatua {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Erreserba (berretsita) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Erreserba (berretsita) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          es:
            "{{#isModified}}Modificación: {{/isModified}}{{#reservationStatus.isPending}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Reserva cancelada correctamente {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          de:
            "{{#isModified}}Modifikation: {{/isModified}}{{#reservationStatus.isPending}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Die Buchung wurde richtig storniert {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Buchung (bestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Buchung (bestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          it:
            "{{#isModified}}Cambiamento: {{/isModified}}{{#reservationStatus.isPending}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}La prenotazione è stata cancellata. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Prenotazione (confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Prenotazione (confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          pt:
            "{{#isModified}}Modificação: {{/isModified}}{{#reservationStatus.isPending}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}A reserva foi cancelada. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ru:
            "{{#isModified}}модификация: {{/isModified}}{{#reservationStatus.isPending}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Бронирование было отменено. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Бронирование (подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Бронирование (подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          nl:
            "{{#isModified}}Wijziging: {{/isModified}}{{#reservationStatus.isPending}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}De reservering is geannuleerd. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Boeking (bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Boeking (bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ko:
            "{{#isModified}}수정: {{/isModified}}{{#reservationStatus.isPending}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}이 예약은 취소되었습니다 {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}예약 (확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}예약 (확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          da:
            "{{#isModified}}Ændring: {{/isModified}}{{#reservationStatus.isPending}}Reservation (ikke bekræftet) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Reservation annulleret korrekt {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reservation (bekræftet) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reservation (bekræftet) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reservation (ikke bekræftet) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reservation (ikke bekræftet) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          hu:
            "{{#isModified}}Frissítés: {{/isModified}}{{#reservationStatus.isPending}}Foglalás (nincs megerősítve) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}A foglalás törölve lett. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Foglalás (megerősítve) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Foglalás (megerősítve) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Foglalás (nincs megerősítve) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Foglalás (nincs megerősítve) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ja:
            "{{#isModified}}変更: {{/isModified}}{{#reservationStatus.isPending}}予約（未確認） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}予約は正常にキャンセルされました {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}予約（確認済み） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}予約（確認済み） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}予約（未確認） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}予約（未確認） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ro:
            "{{#isModified}}Modificare: {{/isModified}}{{#reservationStatus.isPending}}Rezervare (Neconfirmată) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Rezervare anulată cu succes {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Rezervare (confirmată) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Rezervare (confirmată) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Rezervare (Neconfirmată) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Rezervare (Neconfirmată) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          zh:
            "{{#isModified}}更新: {{/isModified}}{{#reservationStatus.isPending}}预订（未确认） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}预订已被取消。 {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}预订（已确认） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}预订（已确认） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}预订（未确认） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}预订（未确认） {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
        },
        visible: ({ formValues, userInfo }) => {
          if (
            isCartAbandonmentType(formValues.type) ||
            isUpsellAvailableType(formValues.type) ||
            isQuotationPriceType(formValues.type) ||
            isWaitingListAvailableType(formValues.type) ||
            isLoyaltyWelcomeType(formValues.type) ||
            isLoyaltyWelcomeValidatedType(formValues.type) ||
            isReservationFailedType(formValues.type) ||
            isReservationAbandonedPaymentType(formValues.type)
          ) {
            return false;
          }
          switch (formValues.templateType) {
            case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM:
              return userInfo.isWitAdmin || !isReservationConfirmationType(formValues.type);
            default:
            case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT:
              return false;
          }
        },
      },
      {
        fieldName: "cartAbandonmentSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.cartAbandonment,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isCartAbandonmentType(formValues.type)
          );
        },
      },
      {
        fieldName: "paymentFailedSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.paymentFailed,
        visible: ({ formValues }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            isReservationFailedType(formValues.type)
          );
        },
      },
      {
        fieldName: "abandonedPaymentSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.abandonedPayment,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isReservationAbandonedPaymentType(formValues.type)
          );
        },
      },
      {
        fieldName: "upsellSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.upsell,
        visible: ({ formValues }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            isUpsellAvailableType(formValues.type)
          );
        },
      },
      {
        fieldName: "quotationSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.quotation,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isQuotationPriceType(formValues.type)
          );
        },
      },
      {
        fieldName: "waitingListSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.waitingList,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isWaitingListAvailableType(formValues.type)
          );
        },
      },
      {
        fieldName: "welcomeLoyaltySubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.loyaltyWelcome,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isLoyaltyWelcomeType(formValues.type)
          );
        },
      },
      {
        fieldName: "agenciesSubject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: DEFAULT_SUBJECTS.agencies,
        visible: ({ formValues, userInfo }) => {
          return (
            formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            userInfo.isWitAdmin &&
            isLoyaltyWelcomeValidatedType(formValues.type)
          );
        },
      },
      {
        fieldName: "body",
        displayName: "body",
        translated: true,
        visible: ({ formValues }) => {
          return formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM;
        },
        transform: ({ field, formValues }) => {
          return {
            ...field,
            fullPage: true,
            widget:
              isPreStayType(formValues.type) || isPostStayType(formValues.type)
                ? "richHTMLEditor"
                : "textarea",
          };
        },
      },
      {
        fieldName: "header",
        displayName: "Header",
        widget: "richHTMLEditor",
        translated: true,
        defaultValue: {
          en: "<p>{nombre_cliente}</p>",
          ca: "<p>{nombre_cliente}</p>",
          fr: "<p>{nombre_cliente}</p>",
          eu: "<p>{nombre_cliente}</p>",
          es: "<p>{nombre_cliente}</p>",
          de: "<p>{nombre_cliente}</p>",
          it: "<p>{nombre_cliente}</p>",
          pt: "<p>{nombre_cliente}</p>",
          ru: "<p>{nombre_cliente}</p>",
          nl: "<p>{nombre_cliente}</p>",
          ko: "<p>{nombre_cliente}</p>",
          da: "<p>{nombre_cliente}</p>",
          hu: "<p>{nombre_cliente}</p>",
          ja: "<p>{nombre_cliente}</p>",
          ro: "<p>{nombre_cliente}</p>",
          zh: "<p>{nombre_cliente}</p>",
        },
        visible: ({ formValues }) => {
          switch (formValues.templateType) {
            case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM:
              return false;
            default:
              return (
                !isCartAbandonmentType(formValues.type) &&
                !isUpsellAvailableType(formValues.type) &&
                !isQuotationPriceType(formValues.type) &&
                !isWaitingListAvailableType(formValues.type) &&
                !isLoyaltyWelcomeType(formValues.type) &&
                !isLoyaltyWelcomeValidatedType(formValues.type) &&
                !isReservationFailedType(formValues.type) &&
                !isReservationAbandonedPaymentType(formValues.type)
              );
          }
        },
      },
      {
        fieldName: "footer",
        displayName: "Footer",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ formValues }) => {
          return (
            formValues.templateType !== CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM &&
            !isCartAbandonmentType(formValues.type) &&
            !isUpsellAvailableType(formValues.type) &&
            !isQuotationPriceType(formValues.type) &&
            !isWaitingListAvailableType(formValues.type) &&
            !isLoyaltyWelcomeType(formValues.type) &&
            !isLoyaltyWelcomeValidatedType(formValues.type) &&
            !isReservationFailedType(formValues.type) &&
            !isReservationAbandonedPaymentType(formValues.type)
          );
        },
      },
      // Marker that fixes where the Traducciones section renders. Placing it BEFORE the property
      // selector makes the selector appear BETWEEN translations and the Save/Back buttons
      // (matching Conditions/CancellationPolicy). Without it, EntityForm renders all non-translated
      // fields (incl. the selector) first and the translations section last.
      {
        translationsList: true,
      },
      // ── AGR-only: property selector ──────────────────────────────────────────────
      // Visible only when _isChain is injected into formValues by EmailTemplateForm's mapper.
      // The dual list itself is rendered by EmailTemplateForm's `selectedMotors` custom renderer
      // (reactive: options/disabled/selection come from component state, not from here).
      {
        fieldName: "selectedMotorsSection",
        displayName: "Property selection",
        widget: "section",
        visible: ({ formValues }) => !!formValues._isChain,
      },
      {
        fieldName: "selectedMotors",
        displayName: "",
        widget: "dualListBox",
        defaultValue: [],
        visible: ({ formValues }) => !!formValues._isChain,
      },
    ],
  },
  validation: {
    id: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    hotelTicker: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    type: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.BACKEND_ERROR,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_FAILURE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_ORANGE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.GIFT_CARD,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PURCHASE_EVENT,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.LOYALTY_WELCOME_VALIDATED,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.QUOTATION_PRICE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.WAITING_LIST_AVAILABLE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.UPSELL_AVAILABLE,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
    },
    delay: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    delayCartAbandonment: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 60,
    },
    delayCheckout: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    delayCheckin: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    chronoUnit: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.SECONDS,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.MINUTES,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.HOURS,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.DAYS,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.MINUTES,
    },
    emailDestinationType: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.PROPERTY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
    },
    emailGuestType: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.LOYALTY_GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.NON_LOYALTY_GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.ALL,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.ALL,
    },
    active: {
      type: "Boolean",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    singleMailOnMultipleRoomStays: {
      type: "Boolean",
      constraint: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    subject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    cartAbandonmentSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    paymentFailedSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    abandonedPaymentSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    upsellSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    quotationSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    waitingListSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    welcomeLoyaltySubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    agenciesSubject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    body: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    header: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    footer: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
  },
};

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
